/**
 * Operational saved-view filter controls for dashboard queues.
 */
import { escapeAttr, escapeHtml, labelize } from "./dashboard-html.js";
import { leadFactorySavedViewScopes } from "./dashboard-urls.js";
import {
  contactWorkDefaultCallDailyCapacity,
  contactWorkDefaultLetterDailyCapacity,
  contactWorkDefaultResearchDailyCapacity,
  contactWorkMaxDailyCapacity,
  contactWorkMaxOperatorRosterLength,
  contactWorkMaxPlanTargetDays,
  normalizedContactWorkDailyCapacity,
  normalizedContactWorkOperatorRoster,
  normalizedContactWorkTargetDays
} from "./dashboard-contact-work.js";
import {
  acquisitionPipelineSortOptions,
  assetDispositionSortOptions,
  assetWorkSortOptions,
  booleanFilterOptions,
  contactQueueSortOptions,
  leadFactoryAcquisitionStages,
  leadFactoryAcquisitionWorkQueues,
  leadFactoryContactReadinessStates,
  leadFactoryContactScopes,
  leadFactoryContactWorkLetterKinds,
  leadFactoryContactWorkQueues,
  leadFactoryLeadStatuses,
  leadFactoryOwnedPropertyDispositionStatuses,
  leadFactoryOwnedPropertyDispositionStrategies,
  leadFactoryOwnedPropertyTaskStatuses,
  leadFactoryOwnedPropertyTaskTypes,
  leadFactoryReviewTriageStates,
  leadFactorySavedViewVisibilities,
  leadInventorySortOptions,
  reviewQueueSortOptions,
  workQueueSortOptions,
  type LeadFactorySavedViewVisibility
} from "./dashboard-lead-factory-constants.js";
import type {
  AcquisitionPipelineSavedViewFilters,
  AcquisitionWorkSavedViewFilters,
  AssetDispositionSavedViewFilters,
  AssetWorkSavedViewFilters,
  ContactQueueSavedViewFilters,
  ContactWorkSavedViewFilters,
  LeadFactorySavedView,
  LeadInventorySavedViewFilters,
  ReviewQueueSavedViewFilters,
  SavedViewSortFilters
} from "./dashboard-lead-factory-types.js";

type LeadFactorySavedViewScope = (typeof leadFactorySavedViewScopes)[number];

export function savedViewOptionHtml(view: LeadFactorySavedView): string {
  return `<option value="${escapeAttr(view.id)}" data-saved-view-filters="${escapeAttr(JSON.stringify(view.filters))}">${escapeHtml(view.name)}</option>`;
}

export function leadFactorySavedViewVisibilityOptionsHtml(selected: LeadFactorySavedViewVisibility = "team"): string {
  return leadFactorySavedViewVisibilities
    .map((visibility) => `<option value="${escapeAttr(visibility)}" ${selected === visibility ? "selected" : ""}>${escapeHtml(labelize(visibility))}</option>`)
    .join("");
}

export function textFilterInput(name: string, value: string, label: string, placeholder: string): string {
  return `<input type="text" maxlength="120" value="${escapeAttr(value)}" aria-label="${escapeAttr(label)}" placeholder="${escapeAttr(placeholder)}" data-operational-filter="${escapeAttr(name)}">`;
}

export function limitFilterInput(value: number): string {
  return `<input type="number" min="1" max="500" value="${escapeAttr(value)}" aria-label="Result limit" data-operational-filter="limit">`;
}

export function contactWorkTargetDaysInput(value: number): string {
  return `<input type="number" min="1" max="${escapeAttr(contactWorkMaxPlanTargetDays)}" value="${escapeAttr(normalizedContactWorkTargetDays(value))}" aria-label="Contact work target days" data-operational-filter="targetDays">`;
}

export function contactWorkDailyCapacityInput(name: string, value: number, label: string, defaultCapacity: number): string {
  return `<input type="number" min="1" max="${escapeAttr(contactWorkMaxDailyCapacity)}" value="${escapeAttr(normalizedContactWorkDailyCapacity(value, defaultCapacity))}" aria-label="${escapeAttr(label)}" data-operational-filter="${escapeAttr(name)}">`;
}

export function contactWorkOperatorRosterInput(name: string, value: string, label: string): string {
  return `<input type="text" maxlength="${escapeAttr(contactWorkMaxOperatorRosterLength)}" value="${escapeAttr(normalizedContactWorkOperatorRoster(value))}" aria-label="${escapeAttr(label)}" placeholder="operator@example.com=25, teammate@example.com=25" data-operational-filter="${escapeAttr(name)}">`;
}

export function selectFilterHtml(name: string, value: string, label: string, options: readonly string[]): string {
  return `
    <select aria-label="${escapeAttr(label)}" data-operational-filter="${escapeAttr(name)}">
      <option value="">All</option>
      ${options
        .map(
          (option) => `<option value="${escapeAttr(option)}" ${value === option ? "selected" : ""}>${escapeHtml(labelize(option))}</option>`
        )
        .join("")}
    </select>
  `;
}

export function requiredSelectFilterHtml(name: string, value: string, label: string, options: readonly string[]): string {
  return `
    <select aria-label="${escapeAttr(label)}" data-operational-filter="${escapeAttr(name)}">
      ${options
        .map(
          (option) => `<option value="${escapeAttr(option)}" ${value === option ? "selected" : ""}>${escapeHtml(labelize(option))}</option>`
        )
        .join("")}
    </select>
  `;
}

export function sortControlsHtml(filters: SavedViewSortFilters, options: readonly string[]): string {
  return `
    <select aria-label="Sort field" data-operational-filter="sortBy">
      <option value="">Default sort</option>
      ${options
        .map((option) => `<option value="${escapeAttr(option)}" ${filters.sortBy === option ? "selected" : ""}>${escapeHtml(labelize(option))}</option>`)
        .join("")}
    </select>
    <select aria-label="Sort direction" data-operational-filter="sortDirection">
      <option value="desc" ${filters.sortDirection === "desc" ? "selected" : ""}>Descending</option>
      <option value="asc" ${filters.sortDirection === "asc" ? "selected" : ""}>Ascending</option>
    </select>
  `;
}

export function operationalSavedViewControlsHtml(
  scope: LeadFactorySavedViewScope,
  views: LeadFactorySavedView[],
  nameValue: string,
  controls: string
): string {
  return `
    <form class="saved-view-controls operational-view-controls" data-operational-saved-view-form="${escapeAttr(scope)}">
      <select aria-label="Saved view" data-operational-saved-view="${escapeAttr(scope)}">
        <option value="">Saved views</option>
        ${views.map(savedViewOptionHtml).join("")}
      </select>
      <button class="button secondary saved-view-share-button" type="button" data-operational-share-view="${escapeAttr(scope)}">Copy link</button>
      <button class="button secondary saved-view-share-button" type="button" data-operational-history-view="${escapeAttr(scope)}">Manage</button>
      <button class="button secondary saved-view-share-button" type="button" data-operational-rename-view="${escapeAttr(scope)}">Rename</button>
      <button class="button secondary saved-view-share-button" type="button" data-operational-delete-view="${escapeAttr(scope)}">Delete</button>
      <button class="button secondary saved-view-share-button" type="button" data-operational-grant-view="${escapeAttr(scope)}">Grant access</button>
      <button class="button secondary saved-view-share-button" type="button" data-operational-revoke-view="${escapeAttr(scope)}">Revoke access</button>
      ${controls}
      <select aria-label="Saved view visibility" data-operational-saved-view-visibility>
        ${leadFactorySavedViewVisibilityOptionsHtml()}
      </select>
      <input type="text" maxlength="80" value="${escapeAttr(nameValue)}" aria-label="Saved view name" data-operational-saved-view-name>
      <button class="button secondary" type="button" data-operational-filter-apply="${escapeAttr(scope)}">Apply</button>
      <button class="button secondary" type="submit">Save view</button>
    </form>
  `;
}

export function leadInventorySavedViewControlsHtml(views: LeadFactorySavedView[], filters: LeadInventorySavedViewFilters): string {
  return operationalSavedViewControlsHtml(
    "lead_inventory",
    views,
    `${filters.status ? labelize(filters.status) : "All leads"} view`,
    `
      ${selectFilterHtml("status", filters.status, "Lead status", leadFactoryLeadStatuses)}
      ${textFilterInput("assignedTo", filters.assignedTo, "Lead assignee", "Assignee")}
      ${limitFilterInput(filters.limit)}
      ${sortControlsHtml(filters, leadInventorySortOptions)}
    `
  );
}

export function reviewQueueSavedViewControlsHtml(views: LeadFactorySavedView[], filters: ReviewQueueSavedViewFilters): string {
  return operationalSavedViewControlsHtml(
    "review_queue",
    views,
    `${filters.triageState ? labelize(filters.triageState) : "All review"} view`,
    `
      ${selectFilterHtml("triageState", filters.triageState, "Review triage", leadFactoryReviewTriageStates)}
      ${textFilterInput("assignedTo", filters.assignedTo, "Review assignee", "Assignee")}
      ${limitFilterInput(filters.limit)}
      ${sortControlsHtml(filters, reviewQueueSortOptions)}
    `
  );
}

export function contactQueueSavedViewControlsHtml(views: LeadFactorySavedView[], filters: ContactQueueSavedViewFilters): string {
  return operationalSavedViewControlsHtml(
    "contact_queue",
    views,
    `${filters.readiness ? labelize(filters.readiness) : "All ready leads"} view`,
    `
      ${selectFilterHtml("readiness", filters.readiness, "Contact readiness", leadFactoryContactReadinessStates)}
      ${requiredSelectFilterHtml("scope", filters.scope || "lead_supply", "Contact source scope", leadFactoryContactScopes)}
      ${requiredSelectFilterHtml("excludeTest", filters.excludeTest || "true", "Exclude test contact rows", booleanFilterOptions)}
      ${limitFilterInput(filters.limit)}
      ${sortControlsHtml(filters, contactQueueSortOptions)}
    `
  );
}

export function contactWorkSavedViewControlsHtml(views: LeadFactorySavedView[], filters: ContactWorkSavedViewFilters): string {
  return operationalSavedViewControlsHtml(
    "contact_work",
    views,
    `${filters.queue && filters.queue !== "all" ? labelize(filters.queue) : "All contact work"} view`,
    `
      ${selectFilterHtml("queue", filters.queue === "all" ? "" : filters.queue, "Contact work lane", leadFactoryContactWorkQueues.filter((queue) => queue !== "all"))}
      ${requiredSelectFilterHtml("letterKind", filters.letterKind || "all", "Letter work kind", leadFactoryContactWorkLetterKinds)}
      ${requiredSelectFilterHtml("scope", filters.scope || "lead_supply", "Contact work source scope", leadFactoryContactScopes)}
      ${requiredSelectFilterHtml("excludeTest", filters.excludeTest || "true", "Exclude test contact work", booleanFilterOptions)}
      ${requiredSelectFilterHtml("actionableOnly", filters.actionableOnly || "true", "Show actionable contact work", booleanFilterOptions)}
      ${textFilterInput("assignedTo", filters.assignedTo, "Contact work assignee", "Assignee")}
      ${contactWorkTargetDaysInput(filters.targetDays)}
      ${contactWorkDailyCapacityInput("callDailyCapacity", filters.callDailyCapacity, "Call daily capacity", contactWorkDefaultCallDailyCapacity)}
      ${contactWorkDailyCapacityInput("letterDailyCapacity", filters.letterDailyCapacity, "Letter daily capacity", contactWorkDefaultLetterDailyCapacity)}
      ${contactWorkDailyCapacityInput("researchDailyCapacity", filters.researchDailyCapacity, "Research daily capacity", contactWorkDefaultResearchDailyCapacity)}
      ${contactWorkOperatorRosterInput("callOperatorRoster", filters.callOperatorRoster, "Call operator roster")}
      ${contactWorkOperatorRosterInput("letterOperatorRoster", filters.letterOperatorRoster, "Letter operator roster")}
      ${contactWorkOperatorRosterInput("researchOperatorRoster", filters.researchOperatorRoster, "Research operator roster")}
      <button class="button secondary" type="button" data-contact-work-my-work>My Work</button>
      ${limitFilterInput(filters.limit)}
      ${sortControlsHtml(filters, workQueueSortOptions)}
    `
  );
}

export function acquisitionPipelineSavedViewControlsHtml(
  views: LeadFactorySavedView[],
  filters: AcquisitionPipelineSavedViewFilters
): string {
  return operationalSavedViewControlsHtml(
    "acquisition_pipeline",
    views,
    `${filters.stage ? labelize(filters.stage) : "All pipeline"} view`,
    `
      ${selectFilterHtml("stage", filters.stage, "Acquisition stage", leadFactoryAcquisitionStages)}
      ${limitFilterInput(filters.limit)}
      ${sortControlsHtml(filters, acquisitionPipelineSortOptions)}
    `
  );
}

export function acquisitionWorkSavedViewControlsHtml(views: LeadFactorySavedView[], filters: AcquisitionWorkSavedViewFilters): string {
  return operationalSavedViewControlsHtml(
    "acquisition_work",
    views,
    `${filters.queue && filters.queue !== "all" ? labelize(filters.queue) : "All acquisition work"} view`,
    `
      ${selectFilterHtml("queue", filters.queue === "all" ? "" : filters.queue, "Acquisition work lane", leadFactoryAcquisitionWorkQueues.filter((queue) => queue !== "all"))}
      ${limitFilterInput(filters.limit)}
      ${sortControlsHtml(filters, workQueueSortOptions)}
    `
  );
}

export function assetWorkSavedViewControlsHtml(views: LeadFactorySavedView[], filters: AssetWorkSavedViewFilters): string {
  return operationalSavedViewControlsHtml(
    "asset_work",
    views,
    `${labelize(filters.status || "assigned")} asset work view`,
    `
      ${requiredSelectFilterHtml("status", filters.status || "assigned", "Asset task status", leadFactoryOwnedPropertyTaskStatuses)}
      ${selectFilterHtml("taskType", filters.taskType, "Asset task type", leadFactoryOwnedPropertyTaskTypes)}
      ${limitFilterInput(filters.limit)}
      ${sortControlsHtml(filters, assetWorkSortOptions)}
    `
  );
}

export function assetDispositionSavedViewControlsHtml(
  views: LeadFactorySavedView[],
  filters: AssetDispositionSavedViewFilters
): string {
  return operationalSavedViewControlsHtml(
    "asset_disposition",
    views,
    `${filters.status ? labelize(filters.status) : "All disposition"} view`,
    `
      ${selectFilterHtml("status", filters.status, "Disposition status", leadFactoryOwnedPropertyDispositionStatuses)}
      ${selectFilterHtml("strategy", filters.strategy, "Disposition strategy", leadFactoryOwnedPropertyDispositionStrategies)}
      ${limitFilterInput(filters.limit)}
      ${sortControlsHtml(filters, assetDispositionSortOptions)}
    `
  );
}

